import type { AdminOrganizationDetail } from '../../../../domain/superadmin/types';
import { formatCurrency, formatDate } from '../../../../utils/formatters';
import DashboardCard from '../../../dashboard/components/DashboardCard';
import { cn } from '@/lib/utils';
import { SectionEmpty } from './SectionEmpty';

type BillingSectionProps = {
  products: AdminOrganizationDetail['products'];
  invoices: AdminOrganizationDetail['invoices'];
};

const formatLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

function invoiceStatusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'paid') return 'border-success/30 bg-success-bg text-success-text';
  if (normalized === 'open' || normalized === 'draft') {
    return 'border-warning/30 bg-warning-bg text-warning-text';
  }
  return 'border-border bg-surface-subtle text-secondary';
}

export function BillingSection({ products, invoices }: BillingSectionProps) {
  return (
    <div className="mb-s5 grid gap-s5 lg:grid-cols-2">
      <DashboardCard title="Produtos contratados">
        {products.length === 0 ? (
          <SectionEmpty message="Nenhum produto contratado registrado." />
        ) : (
          <ul className="flex flex-col gap-s2">
            {products.map((product, index) => (
              <li
                key={`${product.label}-${index}`}
                className="flex items-center justify-between gap-s3 rounded-lg border border-subtle bg-surface px-s4 py-3"
              >
                <span className="text-[13px] font-medium text-default">{product.label}</span>
                <span className="font-mono text-[13px] tabular-nums text-default">
                  {formatCurrency(product.amountCents / 100)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DashboardCard>

      <DashboardCard title="Histórico de faturamento">
        {invoices.length === 0 ? (
          <SectionEmpty message="Nenhuma fatura encontrada para esta empresa." />
        ) : (
          <ul className="flex flex-col gap-s2">
            {invoices.map((invoice) => (
              <li
                key={invoice.id}
                className="rounded-lg border border-subtle bg-surface px-s4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-s2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-default">
                      {invoice.number ? `Fatura #${invoice.number}` : 'Fatura'}
                    </p>
                    <p className="mt-0.5 font-mono text-[11.5px] tabular-nums text-muted">
                      {invoice.paidAt ? formatDate(invoice.paidAt) : '—'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-mono text-[13px] tabular-nums text-default">
                      {formatCurrency(invoice.amountPaidCents / 100)}
                    </span>
                    <span
                      className={cn(
                        'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                        invoiceStatusClass(invoice.status),
                      )}
                    >
                      {formatLabel(invoice.status)}
                    </span>
                  </div>
                </div>
                {(invoice.hostedInvoiceUrl || invoice.invoicePdf) && (
                  <div className="mt-2 flex flex-wrap gap-s3 text-[12px]">
                    {invoice.hostedInvoiceUrl && (
                      <a
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Ver online
                      </a>
                    )}
                    {invoice.invoicePdf && (
                      <a
                        href={invoice.invoicePdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        PDF
                      </a>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DashboardCard>
    </div>
  );
}
